import React, { useState, useMemo } from 'react';
import {
  Button, message, Alert, Typography, Tag, Descriptions,
  Spin, Form, Input, InputNumber, Switch, Select, Divider, Tooltip,
} from 'antd';
import {
  PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FontSizeOutlined, NumberOutlined, CheckSquareOutlined,
  UnorderedListOutlined, LockOutlined, MailOutlined, CalendarOutlined,
  ExclamationCircleFilled, FieldStringOutlined, PlusOutlined, MinusCircleOutlined,
} from '@ant-design/icons';
import { OPMApi } from '../../../../api/opmApi';
import type { ExecuteProcessResponse } from '../../../../api/opmApi';

const { Text } = Typography;

interface ExecutionRunPanelProps {
  ontologyId: number;
  processNodeId: string;
  inputSchema: any[] | Record<string, any> | null;
  outputSchema?: any;
  onClose: () => void;
  onExecutionComplete?: () => void;
}

type OutputFieldDef = { name: string; codeName: string; description?: string; type: string };

function buildOutputLookup(outputSchema: any): Record<string, OutputFieldDef> {
  const lookup: Record<string, OutputFieldDef> = {};
  if (!outputSchema) return lookup;
  const schema = Array.isArray(outputSchema) ? outputSchema[0] : outputSchema;
  if (!schema) return lookup;
  const props: Record<string, any> = schema.properties || {};
  Object.entries(props).forEach(([key, def]: [string, any]) => {
    const codeName = def.codeName || key;
    lookup[codeName] = {
      name: def.name || codeName,
      codeName,
      description: def.description || undefined,
      type: def.type || 'string',
    };
  });
  return lookup;
}

function renderOutputValue(value: any, type: string): React.ReactNode {
  if (value === null || value === undefined) {
    return <Text type="secondary" style={{ fontSize: 12 }}>null</Text>;
  }
  if (typeof value === 'object') {
    return (
      <pre style={{
        margin: 0, fontSize: 12, fontFamily: 'monospace',
        background: '#fafafa', borderRadius: 4, padding: '4px 8px',
        whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (type === 'boolean') {
    return <Tag color={value ? 'success' : 'default'}>{String(value)}</Tag>;
  }
  return <span style={{ fontSize: 13, fontFamily: type === 'string' ? 'inherit' : 'monospace' }}>{String(value)}</span>;
}

const StructuredOutput: React.FC<{ outputPayload: Record<string, any>; outputSchema: any }> = ({
  outputPayload, outputSchema,
}) => {
  const lookup = useMemo(() => buildOutputLookup(outputSchema), [outputSchema]);
  const entries = Object.entries(outputPayload);
  if (entries.length === 0) {
    return <Text type="secondary" style={{ fontSize: 12 }}>（无返回数据）</Text>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(([key, value]) => {
        const def = lookup[key] || { name: key, codeName: key, type: typeof value === 'number' ? 'number' : 'string' };
        const showCode = def.codeName && def.codeName !== def.name;
        return (
          <div key={key} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '6px 10px', background: '#fff',
            border: '1px solid #e8e8e8', borderRadius: 6,
          }}>
            <div style={{ width: 110, flexShrink: 0, paddingTop: 2 }}>
              <div style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{def.name}</div>
              {showCode && (
                <div style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace', lineHeight: 1.3 }}>{def.codeName}</div>
              )}
              {def.description && (
                <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.3 }}>{def.description}</div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
              {renderOutputValue(value, def.type)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function convertToJsonSchema(inputSchema: any[] | Record<string, any> | null): {
  schema: Record<string, any>;
  hasFields: boolean;
} {
  if (!inputSchema) return { schema: { type: 'object', properties: {} }, hasFields: false };
  if (!Array.isArray(inputSchema)) {
    return { schema: inputSchema, hasFields: Object.keys(inputSchema.properties || {}).length > 0 };
  }
  if (inputSchema.length === 0) return { schema: { type: 'object', properties: {} }, hasFields: false };

  const properties: Record<string, any> = {};
  const required: string[] = [];
  const scalars = ['minLength', 'maxLength', 'pattern', 'format',
    'minimum', 'maximum', 'multipleOf', 'minItems', 'maxItems', 'enum'];

  inputSchema.forEach((param) => {
    const propName = param.codeName || param.name;
    if (!propName) return;
    const propSchema: Record<string, any> = { type: param.type, title: param.name, codeName: param.codeName };
    if (param.description) propSchema.description = param.description;
    if (param.isArray) propSchema.isArray = true;
    scalars.forEach((c) => { if (param[c] != null) propSchema[c] = param[c]; });
    if (param.type === 'object' && param.properties) {
      const childProps: Record<string, any> = {};
      const childReq: string[] = [];
      Object.entries(param.properties).forEach(([k, v]: [string, any]) => {
        const childKey = v.codeName || k;
        const cs: Record<string, any> = { type: v.type, title: v.name || k, codeName: v.codeName || k };
        if (v.description) cs.description = v.description;
        scalars.forEach((c) => { if (v[c] != null) cs[c] = v[c]; });
        childProps[childKey] = cs;
        if (v.required) childReq.push(childKey);
      });
      propSchema.properties = childProps;
      if (childReq.length > 0) propSchema.required = childReq;
    }
    properties[propName] = propSchema;
    if (param.required) required.push(propName);
  });

  const schema: Record<string, any> = { type: 'object', properties };
  if (required.length > 0) schema.required = required;
  return { schema, hasFields: Object.keys(properties).length > 0 };
}

function buildRules(propSchema: Record<string, any>, isRequired: boolean, label: string) {
  const rules: any[] = [];
  if (isRequired) rules.push({ required: true, message: `${label} 为必填项` });
  const { type } = propSchema;
  if (type === 'string') {
    if (propSchema.minLength != null)
      rules.push({ min: propSchema.minLength, message: `至少 ${propSchema.minLength} 个字符` });
    if (propSchema.maxLength != null)
      rules.push({ max: propSchema.maxLength, message: `最多 ${propSchema.maxLength} 个字符` });
    if (propSchema.pattern)
      rules.push({ pattern: new RegExp(propSchema.pattern), message: `${label} 格式不正确` });
    if (propSchema.format === 'email')
      rules.push({ type: 'email', message: '邮箱格式不正确' });
  }
  if (type === 'integer' || type === 'number')
    rules.push({ type: 'number', message: `${label} 必须为数字` });
  return rules;
}

function ConstraintHints({ propSchema, isRequired, fieldKey }: { propSchema: Record<string, any>; isRequired: boolean; fieldKey: string }) {
  const hints: React.ReactNode[] = [];
  const { type, enum: enumVals, minLength, maxLength, minimum, maximum,
    multipleOf, pattern, format, minItems, maxItems } = propSchema;

  const typeMap: Record<string, { icon: React.ReactNode; text: string; color: string }> = {
    string:  { icon: <FontSizeOutlined />,      text: '字符串', color: 'blue' },
    integer: { icon: <NumberOutlined />,        text: '整数',   color: 'purple' },
    number:  { icon: <NumberOutlined />,        text: '数值',   color: 'purple' },
    boolean: { icon: <CheckSquareOutlined />,   text: '布尔',   color: 'cyan' },
    array:   { icon: <UnorderedListOutlined />, text: '数组',   color: 'geekblue' },
    object:  { icon: <FieldStringOutlined />,   text: '对象',   color: 'gold' },
  };
  if (typeMap[type]) {
    const { icon, text, color } = typeMap[type];
    hints.push(<Tag key={`${fieldKey}-type`} icon={icon} color={color} style={{ margin: 0 }}>{text}</Tag>);
  }
  if (isRequired)
    hints.push(<Tag key={`${fieldKey}-req`} icon={<ExclamationCircleFilled />} color="red" style={{ margin: 0 }}>必填</Tag>);
  if (enumVals?.length)
    hints.push(
      <Tooltip key={`${fieldKey}-enum`} title={`可选值：${enumVals.join(' / ')}`}>
        <Tag icon={<UnorderedListOutlined />} color="green" style={{ margin: 0, cursor: 'default' }}>枚举 ×{enumVals.length}</Tag>
      </Tooltip>
    );
  if (minLength != null || maxLength != null) {
    const t = minLength != null && maxLength != null ? `${minLength}~${maxLength} 字符`
      : minLength != null ? `≥${minLength} 字符` : `≤${maxLength} 字符`;
    hints.push(<Tag key={`${fieldKey}-len`} icon={<FontSizeOutlined />} color="blue" style={{ margin: 0 }}>{t}</Tag>);
  }
  if (minimum != null || maximum != null) {
    const t = minimum != null && maximum != null ? `${minimum}~${maximum}`
      : minimum != null ? `≥${minimum}` : `≤${maximum}`;
    hints.push(<Tag key={`${fieldKey}-range`} icon={<NumberOutlined />} color="purple" style={{ margin: 0 }}>{t}</Tag>);
  }
  if (multipleOf != null)
    hints.push(<Tag key={`${fieldKey}-mul`} icon={<NumberOutlined />} color="purple" style={{ margin: 0 }}>{multipleOf} 的倍数</Tag>);
  if (minItems != null || maxItems != null) {
    const t = minItems != null && maxItems != null ? `${minItems}~${maxItems} 项`
      : minItems != null ? `≥${minItems} 项` : `≤${maxItems} 项`;
    hints.push(<Tag key={`${fieldKey}-items`} icon={<UnorderedListOutlined />} color="geekblue" style={{ margin: 0 }}>{t}</Tag>);
  }
  const formatMap: Record<string, React.ReactNode> = {
    email:      <Tag key={`${fieldKey}-fmt`} icon={<MailOutlined />}     color="cyan" style={{ margin: 0 }}>邮箱</Tag>,
    date:       <Tag key={`${fieldKey}-fmt`} icon={<CalendarOutlined />} color="cyan" style={{ margin: 0 }}>日期</Tag>,
    'date-time':<Tag key={`${fieldKey}-fmt`} icon={<CalendarOutlined />} color="cyan" style={{ margin: 0 }}>日期时间</Tag>,
    uri:        <Tag key={`${fieldKey}-fmt`} icon={<LockOutlined />}     color="cyan" style={{ margin: 0 }}>URI</Tag>,
    uuid:       <Tag key={`${fieldKey}-fmt`} icon={<LockOutlined />}     color="cyan" style={{ margin: 0 }}>UUID</Tag>,
  };
  if (format && formatMap[format]) hints.push(formatMap[format]);
  if (pattern)
    hints.push(
      <Tooltip key={`${fieldKey}-pat`} title={`正则：${pattern}`}>
        <Tag icon={<LockOutlined />} color="orange" style={{ margin: 0, cursor: 'default' }}>正则校验</Tag>
      </Tooltip>
    );

  if (hints.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', flexShrink: 0, alignItems: 'center' }}>
      {hints}
    </div>
  );
}

function renderControl(propSchema: Record<string, any>, label: string) {
  const { type, enum: enumVals } = propSchema;
  if (enumVals?.length)
    return <Select options={enumVals.map((v: any) => ({ label: String(v), value: v }))} placeholder="请选择" />;
  if (type === 'boolean') return <Switch />;
  if (type === 'integer') return <InputNumber style={{ width: '100%' }} precision={0} />;
  if (type === 'number') return <InputNumber style={{ width: '100%' }} />;
  return <Input placeholder={`请输入${label}`} />;
}

function FieldRow({ fieldName, propSchema, isRequired, namePath, fieldKey }: {
  fieldName: string;
  propSchema: Record<string, any>;
  isRequired: boolean;
  namePath: (string | number)[];
  fieldKey: string;
}) {
  const label = propSchema.title || fieldName;
  const codeName = propSchema.codeName && propSchema.codeName !== label ? propSchema.codeName : null;
  const desc = propSchema.description;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
      <div style={{
        width: 100, flexShrink: 0, textAlign: 'right', paddingTop: 5,
      }}>
        {isRequired && <span style={{ color: '#ff4d4f', marginRight: 2 }}>*</span>}
        <span style={{ fontSize: 13, color: '#333' }}>{label}</span>
        {codeName && (
          <div style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace', lineHeight: 1.3 }}>{codeName}</div>
        )}
        {desc && (
          <div style={{ fontSize: 11, color: '#aaa', lineHeight: 1.3 }}>{desc}</div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Form.Item
          name={namePath}
          rules={buildRules(propSchema, isRequired, label)}
          valuePropName={propSchema.type === 'boolean' ? 'checked' : 'value'}
          style={{ marginBottom: 0 }}
        >
          {renderControl(propSchema, label)}
        </Form.Item>
      </div>
      <div style={{ paddingTop: 5 }}>
        <ConstraintHints propSchema={propSchema} isRequired={isRequired} fieldKey={fieldName} />
      </div>
    </div>
  );
}

function ArrayFieldRow({ fieldName, propSchema, isRequired }: {
  fieldName: string;
  propSchema: Record<string, any>;
  isRequired: boolean;
}) {
  const label = propSchema.title || fieldName;
  const codeName = propSchema.codeName && propSchema.codeName !== label ? propSchema.codeName : null;
  const desc = propSchema.description;
  const { minItems, maxItems } = propSchema;
  const isObjectType = propSchema.type === 'object' && propSchema.properties;
  const itemSchema: Record<string, any> = { ...propSchema, isArray: false };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {isRequired && <span style={{ color: '#ff4d4f' }}>*</span>}
        <span style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>{label}</span>
        {codeName && <span style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace' }}>{codeName}</span>}
        <Tag color="geekblue" style={{ fontSize: 11, margin: 0 }}>数组</Tag>
        {desc && <span style={{ fontSize: 11, color: '#aaa' }}>{desc}</span>}
        <ConstraintHints propSchema={propSchema} isRequired={false} fieldKey={fieldName} />
      </div>
      <Form.List name={fieldName}>
        {(fields, { add, remove }) => (
          <div style={{ paddingLeft: 16, borderLeft: '2px solid #e8e8e8' }}>
            {fields.map(({ key, name }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  {isObjectType ? (
                    <div style={{ paddingLeft: 8, borderLeft: '1px dashed #d9d9d9' }}>
                      {Object.entries(propSchema.properties).map(([ck, cs]: [string, any]) => {
                        const childReq: string[] = propSchema.required || [];
                        return (
                          <FieldRow key={ck} fieldName={ck} propSchema={cs as Record<string, any>}
                            isRequired={childReq.includes(ck)} namePath={[name, ck]} fieldKey={`${fieldName}_${name}_${ck}`} />
                        );
                      })}
                    </div>
                  ) : (
                    <Form.Item name={name} style={{ marginBottom: 0 }}
                      rules={buildRules(itemSchema, false, `第${name + 1}项`)}>
                      {renderControl(itemSchema, label)}
                    </Form.Item>
                  )}
                </div>
                <Button size="small" danger type="text" icon={<MinusCircleOutlined />}
                  onClick={() => remove(name)} style={{ marginTop: 4 }} />
              </div>
            ))}
            <Button size="small" type="dashed" block icon={<PlusOutlined />}
              onClick={() => add(isObjectType ? {} : undefined)}
              disabled={maxItems != null && fields.length >= maxItems}>
              添加元素
            </Button>
            {minItems != null && fields.length < minItems && (
              <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>至少需要 {minItems} 个元素</div>
            )}
          </div>
        )}
      </Form.List>
    </div>
  );
}

const SchemaFormFields: React.FC<{ schema: Record<string, any> }> = ({ schema }) => {
  const { properties = {}, required = [] } = schema;
  return (
    <>
      {Object.entries(properties).map(([key, propSchema]: [string, any]) => {
        const label = propSchema.title || key;
        const isRequired = required.includes(key);
        if (propSchema.isArray) {
          return <ArrayFieldRow key={key} fieldName={key} propSchema={propSchema} isRequired={isRequired} />;
        }
        if (propSchema.type === 'object' && propSchema.properties) {
          const requiredChildren: string[] = propSchema.required || [];
          return (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                {isRequired && <span style={{ color: '#ff4d4f' }}>*</span>}
                <Text style={{ fontSize: 13, fontWeight: 500 }}>{label}</Text>
                <Tag color="gold" style={{ fontSize: 11, margin: 0 }}>对象</Tag>
              </div>
              <div style={{ paddingLeft: 16, borderLeft: '2px solid #e8e8e8' }}>
                {Object.entries(propSchema.properties).map(([ck, cs]: [string, any]) => (
                  <FieldRow key={ck} fieldName={ck} propSchema={cs as Record<string, any>}
                    isRequired={requiredChildren.includes(ck)} namePath={[key, ck]} fieldKey={`${key}_${ck}`} />
                ))}
              </div>
            </div>
          );
        }
        return (
          <FieldRow key={key} fieldName={key} propSchema={propSchema}
            isRequired={isRequired} namePath={[key]} fieldKey={key} />
        );
      })}
    </>
  );
};

const ExecutionRunPanel: React.FC<ExecutionRunPanelProps> = ({
  ontologyId, processNodeId, inputSchema, outputSchema, onClose, onExecutionComplete,
}) => {
  const [form] = Form.useForm();
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ExecuteProcessResponse | null>(null);

  const { schema: jsonSchema, hasFields } = convertToJsonSchema(inputSchema);

  const handleExecute = async () => {
    let payload: Record<string, any> = {};
    if (hasFields) {
      try { payload = await form.validateFields(); }
      catch { return; }
    }
    setExecuting(true);
    setResult(null);
    try {
      const response = await OPMApi.executeProcess(ontologyId, processNodeId, {
        processNodeId,
        inputPayload: payload,
        triggerType: 'MANUAL',
      });
      setResult(response);
      if (response.status === 'SUCCESS') {
        message.success(`执行成功，耗时 ${response.executionTimeMs}ms`);
      } else {
        message.error(`执行失败: ${response.errorMessage || response.status}`);
      }
      onExecutionComplete?.();
    } catch (error: any) {
      message.error(error.message || '执行失败');
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setResult(null);
    onClose();
  };

  return (
    <div>
      <Divider style={{ margin: '12px 0 0' }}>
        <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400, userSelect: 'none' }}>
          试运行
        </span>
        <Button
          type="link" size="small" danger
          style={{ fontSize: 12, padding: '0 4px', marginLeft: 4 }}
          onClick={handleClose}
        >
          收起
        </Button>
      </Divider>

      <Spin spinning={executing}>
        <div style={{ paddingTop: 8 }}>
          {/* 输入参数表单 */}
          {hasFields ? (
            <Form form={form} component={false}>
              <SchemaFormFields schema={jsonSchema} />
            </Form>
          ) : (
            <Alert message="该函数没有输入参数" description="将以空参数执行"
              type="info" showIcon style={{ marginBottom: 12 }} />
          )}

          {/* 执行按钮 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4, marginBottom: result ? 12 : 0 }}>
            <Button type="primary" icon={<PlayCircleOutlined />} loading={executing} onClick={handleExecute}>
              执行
            </Button>
          </div>

          {/* 执行结果 */}
          {result && (
            <div style={{
              border: `1px solid ${result.status === 'SUCCESS' ? '#b7eb8f' : '#ffccc7'}`,
              borderRadius: 6, padding: 12,
              background: result.status === 'SUCCESS' ? '#f6ffed' : '#fff2f0',
            }}>
              <Descriptions column={2} size="small" style={{ marginBottom: 10 }}>
                <Descriptions.Item label="状态">
                  <Tag
                    color={result.status === 'SUCCESS' ? 'success' : 'error'}
                    icon={result.status === 'SUCCESS' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  >
                    {result.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="耗时">{result.executionTimeMs}ms</Descriptions.Item>
                <Descriptions.Item label="执行ID">{result.executionId}</Descriptions.Item>
                <Descriptions.Item label="重试次数">{result.retryCount}</Descriptions.Item>
              </Descriptions>

              {result.outputPayload && (
                <div style={{ marginBottom: 10 }}>
                  <Text strong style={{ fontSize: 13 }}>函数返回结果</Text>
                  <div style={{ marginTop: 6 }}>
                    <StructuredOutput outputPayload={result.outputPayload} outputSchema={outputSchema} />
                  </div>
                </div>
              )}
              {result.userLogs && (
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 13 }}>用户日志</Text>
                  <pre style={{
                    background: '#fff', border: '1px solid #d9d9d9', borderRadius: 4,
                    padding: 10, marginTop: 6, fontSize: 12, fontFamily: 'monospace',
                    maxHeight: 120, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  }}>{result.userLogs}</pre>
                </div>
              )}
              {result.stderr && (
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ fontSize: 13, color: '#ff4d4f' }}>错误输出</Text>
                  <pre style={{
                    background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4,
                    padding: 10, marginTop: 6, fontSize: 12, fontFamily: 'monospace',
                    color: '#ff4d4f', maxHeight: 100, overflow: 'auto',
                  }}>{result.stderr}</pre>
                  <Text strong style={{ fontSize: 13, color: '#ff4d4f' }}>标准输出</Text>
                  <pre style={{
                    background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4,
                    padding: 10, marginTop: 6, fontSize: 12, fontFamily: 'monospace',
                    color: '#ff4d4f', maxHeight: 100, overflow: 'auto',
                  }}>{result.stdout}</pre>
                </div>
                
              )}
              {result.errorMessage && (
                <Alert message="错误信息" description={result.errorMessage}
                  type="error" showIcon style={{ marginTop: 8 }} />
              )}
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default ExecutionRunPanel;
